class WatchlistAPIManager {
    constructor() {
    }

    static get instance() {
        if (!this._instance) {
            this._instance = new WatchlistAPIManager();
            this._instance._fetchPromise = null;
        }
        return this._instance;
    }

    async getAuthToken() {
        try {
            const session = window.currentSession ||
                (window.AuthAPI && typeof window.AuthAPI.getStoredSession === 'function' ? window.AuthAPI.getStoredSession() : null);
            if (session && session.access_token) return session.access_token;
            if (session && session.app_session) return '__APP_SESSION__';
            return null;
        } catch (_) { return null; }
    }

    async getWatchlists(forceRefresh = false, retryCount = 0) {
        if (this._fetchPromise && !forceRefresh) {
            return this._fetchPromise;
        }

        const token = await this.getAuthToken();
        if (!token) {
            console.warn('[WatchlistAPIManager] No auth token found');
            return { success: false, watchlists: [], message: 'No auth token' };
        }

        const url = '/api/watchlists';
        this._fetchPromise = (async () => {
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.status === 401 && retryCount < 1) {
                    console.warn('[WatchlistAPIManager] 401 Unauthorized, attempting session refresh and retry...');
                    this._fetchPromise = null; // Clear promise before retry
                    if (window.AuthAPI && typeof window.AuthAPI.getSession === 'function') {
                        const refreshRes = await window.AuthAPI.getSession();
                        if (refreshRes && refreshRes.success && refreshRes.session) {
                            // Retry
                            return await this.getWatchlists(true, retryCount + 1);
                        } else {
                            console.warn('[WatchlistAPIManager] Session refresh failed, session may be cleared.');
                            return { success: false, watchlists: [], message: 'Session expired' };
                        }
                    }
                }

                if (!response.ok) {
                    throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();
                return data;

            } catch (error) {
                console.error('[WatchlistAPIManager] Error fetching watchlists:', error);
                this._fetchPromise = null; // Clear on error to allow future retries
                throw error;
            } finally {
                this._fetchPromise = null;
            }
        })();

        return this._fetchPromise;
    }

    async getWatchlistReports(interval, userId, watchlistId) {
        if (!userId || !watchlistId) {
            console.warn('[WatchlistAPIManager] getWatchlistReports: Missing userId or watchlistId');
            return { success: false, message: 'Missing userId or watchlistId' };
        }

        const finalInterval = interval || '1d';

        console.log(`[WatchlistAPIManager] getWatchlistReports: interval=${finalInterval}, userId=${userId}, watchlistId=${watchlistId}`);

        const proxyBaseURL = `${window.location.origin}/proxy-pvtech?url=`;
        // Add timestamp to force fresh fetch (cache busting)
        const targetUrl = `https://api.palosverdestech.com/reports/mwl/${finalInterval}/watchlist/${userId}/${watchlistId}?ts=${Date.now()}`;
        const useProxy = false
        const url = useProxy ? `${proxyBaseURL}${encodeURIComponent(targetUrl)}` : targetUrl

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('[WatchlistAPIManager] Error fetching report data:', error);
            throw error;
        }
    }

    async createWatchlist(name) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            const response = await fetch('/api/watchlists', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name,
                    tickers: [],
                    holdings: {},
                    created_at: new Date().toISOString()
                })
            });
            if (!response.ok) throw new Error('Failed to create watchlist');
            return await response.json();
        } catch (error) {
            console.error('[WatchlistAPIManager] createWatchlist error:', error);
            throw error;
        }
    }

    async deleteWatchlist(id) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            const response = await fetch(`/api/watchlists/${encodeURIComponent(id)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to delete watchlist');
            return { success: true };
        } catch (error) {
            console.error('[WatchlistAPIManager] deleteWatchlist error:', error);
            throw error;
        }
    }

    async renameWatchlist(watchlistId, newName) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            const response = await fetch(`/api/watchlists/${watchlistId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: newName })
            });
            if (response.status === 304) {
                console.log('[WatchlistAPIManager] renameWatchlist: 304 Not Modified (Success)');
                return { success: true };
            }
            if (!response.ok) throw new Error('Failed to rename watchlist');
            return await response.json();
        } catch (error) {
            console.error('[WatchlistAPIManager] renameWatchlist error:', error);
            throw error;
        }
    }

    async addTickerToWatchlist(watchlistId, ticker, quantity = 0, price = 0) {
        return this.addTickersToWatchlist(watchlistId, [{ ticker, quantity, price }]);
    }

    async addTickersToWatchlist(watchlistId, items) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            // 1. Get current watchlists to find the specific one
            const currentData = await this.getWatchlists();
            let watchlists = [];
            if (currentData.watchlists) {
                watchlists = Array.isArray(currentData.watchlists) ? currentData.watchlists : (currentData.watchlists.watchlists || []);
            }

            const watchlist = watchlists.find(w => w.id === watchlistId);
            if (!watchlist) throw new Error('Watchlist not found');

            // 2. Update local object
            if (!watchlist.tickers) watchlist.tickers = [];
            if (!watchlist.holdings || Array.isArray(watchlist.holdings)) watchlist.holdings = {};

            items.forEach(item => {
                if (!watchlist.tickers.includes(item.ticker)) {
                    watchlist.tickers.push(item.ticker);
                }
                watchlist.holdings[item.ticker] = {
                    quantity: parseFloat(item.quantity) || 0,
                    price: parseFloat(item.price) || 0
                };
            });

            // 3. Send PUT request
            const response = await fetch(`/api/watchlists/${encodeURIComponent(watchlistId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: watchlist.name,
                    tickers: watchlist.tickers,
                    holdings: watchlist.holdings
                })
            });

            console.log('[WatchlistAPIManager] PUT payload holdings:', JSON.stringify(watchlist.holdings));

            if (response.status === 304) {
                console.log('[WatchlistAPIManager] addTickersToWatchlist: 304 Not Modified (Success). Using local state.');
                return { success: true, watchlist: watchlist };
            }

            if (!response.ok) throw new Error('Failed to update watchlist');

            // Return success structure that matches expectation
            return { success: true, watchlist: await response.json() };

        } catch (error) {
            console.error('[WatchlistAPIManager] addTickersToWatchlist error:', error);
            throw error;
        }
    }

    async removeTickerFromWatchlist(watchlistId, ticker) {
        return this.removeTickersFromWatchlist(watchlistId, [ticker]);
    }

    async removeTickersFromWatchlist(watchlistId, tickers) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            // 1. Get current watchlists
            const currentData = await this.getWatchlists();
            let watchlists = [];
            if (currentData.watchlists) {
                watchlists = Array.isArray(currentData.watchlists) ? currentData.watchlists : (currentData.watchlists.watchlists || []);
            }

            const watchlist = watchlists.find(w => w.id === watchlistId);
            if (!watchlist) throw new Error('Watchlist not found');

            // 2. Update local object
            console.log(`[WatchlistAPIManager] Removing tickers [${tickers.join(', ')}] from watchlist ${watchlistId} (${watchlist.name})`);
            console.log('[WatchlistAPIManager] Before removal:', [...(watchlist.tickers || [])]);

            if (watchlist.tickers) {
                watchlist.tickers = watchlist.tickers.filter(t => !tickers.includes(t));
            }
            if (watchlist.holdings && typeof watchlist.holdings === 'object' && !Array.isArray(watchlist.holdings)) {
                tickers.forEach(ticker => {
                    if (watchlist.holdings[ticker]) {
                        delete watchlist.holdings[ticker];
                    }
                });
            }

            console.log('[WatchlistAPIManager] After removal:', [...(watchlist.tickers || [])]);

            // 3. Send PUT request
            const payload = {
                name: watchlist.name,
                tickers: watchlist.tickers,
                holdings: watchlist.holdings
            };
            console.log('[WatchlistAPIManager] Sending PUT payload:', JSON.stringify(payload));

            const response = await fetch(`/api/watchlists/${encodeURIComponent(watchlistId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 304) {
                console.log('[WatchlistAPIManager] removeTickersFromWatchlist: 304 Not Modified (Success). Using local state.');
                return { success: true, watchlist: watchlist };
            }

            if (!response.ok) throw new Error('Failed to update watchlist');
            return { success: true, watchlist: await response.json() };

        } catch (error) {
            console.error('[WatchlistAPIManager] removeTickersFromWatchlist error:', error);
            throw error;
        }
    }
    async updateWatchlist(watchlistId, name, tickers, holdings) {
        const token = await this.getAuthToken();
        if (!token) return { success: false, message: 'No auth token' };

        try {
            const response = await fetch(`/api/watchlists/${encodeURIComponent(watchlistId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: name,
                    tickers: tickers,
                    holdings: holdings
                })
            });

            if (response.status === 304) {
                console.log('[WatchlistAPIManager] updateWatchlist: 304 Not Modified (Success).');
                return { success: true };
            }

            if (!response.ok) throw new Error('Failed to update watchlist');
            return { success: true, watchlist: await response.json() };

        } catch (error) {
            console.error('[WatchlistAPIManager] updateWatchlist error:', error);
            throw error;
        }
    }
}

window.WatchlistAPIManager = WatchlistAPIManager;
